import { memo, useCallback } from 'react';
import { ChevronRight } from 'lucide-react';
import Tooltip from '../Tooltip.jsx';
import { useSidebarContext } from './Sidebar.jsx';
import { resolveSidebarVariant, sidebarItemTooltipProps } from './sidebarVariants.js';

const LABEL_MAX_LENGTH = 26;

const ICON_BOX_CLASS =
  'flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent p-1.5 transition-colors duration-150';
const ICON_SIZE = 20;
const CHEVRON_SIZE = 18;

/**
 * Navigation item (Figma: Sidebar Buttons / Sidebar Button + Text).
 */
const SidebarItem = ({
  icon: IconComp,
  label = '',
  itemId,
  hasSubmenu = false,
  submenuOpen = false,
  onSubmenuToggle,
  badge,
  onClick,
  tabIndex = 0,
  onFocus,
  className = '',
}) => {
  const { expanded, activeItem, setActiveItem, variant } = useSidebarContext();
  const palette = resolveSidebarVariant(variant);
  const isActive = activeItem === itemId;
  const displayLabel =
    label.length > LABEL_MAX_LENGTH ? `${label.slice(0, LABEL_MAX_LENGTH)}…` : label;

  const handleClick = useCallback(() => {
    if (hasSubmenu && onSubmenuToggle) onSubmenuToggle();
    setActiveItem?.(itemId);
    onClick?.();
  }, [hasSubmenu, onSubmenuToggle, setActiveItem, itemId, onClick]);

  const iconButtonActive = [ICON_BOX_CLASS, palette.iconActive].join(' ');
  const iconButtonInactive = [
    ICON_BOX_CLASS,
    !expanded ? palette.collapsedIconHover : '',
    palette.iconInactive,
    !expanded ? palette.iconHoverBorder : '',
    !expanded ? palette.iconHoverShadow : '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconButton = (
    <span className={isActive ? iconButtonActive : iconButtonInactive}>
      {IconComp && (
        <IconComp size={ICON_SIZE} strokeWidth={isActive ? 2.25 : 2} aria-hidden />
      )}
    </span>
  );

  const row = (
    <button
      type="button"
      data-nav-item={itemId}
      tabIndex={tabIndex}
      onFocus={onFocus}
      onClick={handleClick}
      // In compressed state the icon is the only visual; aria-label gives SR the name.
      // In expanded state the visible label is sufficient — aria-label would be redundant.
      aria-label={!expanded ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      aria-expanded={hasSubmenu ? submenuOpen : undefined}
      aria-haspopup={hasSubmenu ? 'menu' : undefined}
      className={[
        'group flex w-full items-center transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 focus-visible:ring-offset-black',
        palette.itemFocusRing,
        expanded
          ? `min-h-8 gap-3 pr-1 rounded-lg ${palette.rowHoverBg}`
          : 'justify-center py-0.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {iconButton}

      {expanded && (
        <>
          <span
            className={[
              'min-w-0 flex-1 truncate text-left text-xs font-semibold leading-none',
              isActive
                ? palette.labelActive
                : `${palette.labelInactive} ${palette.labelInactiveHover}`,
            ].join(' ')}
            title={label.length > LABEL_MAX_LENGTH ? label : undefined}
          >
            {displayLabel}
          </span>
          {badge && <span className="ml-1 shrink-0">{badge}</span>}
          {hasSubmenu && (
            <ChevronRight
              size={CHEVRON_SIZE}
              className={[
                'shrink-0 transition-transform duration-150',
                palette.iconChevron,
                submenuOpen ? 'rotate-90' : '',
              ].join(' ')}
              aria-hidden
            />
          )}
        </>
      )}
    </button>
  );

  if (!expanded && label) {
    return (
      <Tooltip
        {...sidebarItemTooltipProps}
        content={label}
        triggerClassName="inline-flex w-full justify-center"
      >
        {row}
      </Tooltip>
    );
  }

  return row;
};

export default memo(SidebarItem);
